<template>
<div class="pdf-pane" ref="pane">
  <!-- <div>{{activeId}}</div> -->
  <div
    v-for="p in pages"
    :key="p.pageNumber"
    class="page-wrap"
  >
    <canvas
      class="pdf-canvas"
      :data-page-number="p.pageNumber"
    />
    <div class="overlay">
      <div
        v-for="h in highlightsByPage(p.pageNumber)"
        :key="h.twFieldPath"
        v-show="isRendered && activeId && activeId.twFieldPath === h.twFieldPath"
        class="hl"
        :style="highlightStyle(h, p.pageNumber)"
      />
    </div>
  </div>
</div>
</template>

<script>
import {
  defineComponent,
  ref,
  reactive,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';

export default defineComponent({
  name: 'PdfCanvasViewer',
  props: {
    // String(URL) or Uint8Array
    pdfUrl: { type: [String, Uint8Array], required: true },
    // Cinnamon OCR highlights
    // [{ ocrFieldSetPage, ocrFieldXCoordinate, ocrFieldYCoordinate, ocrFieldWidth, ocrFieldHeight, twFieldPath, ... }]
    highlights: { type: Array, default: () => [] },
    // activeId が「オブジェクト」(twFieldPath持ち) 前提でもOK
    activeId: { type: [Object, String, Number, null], default: null },
    // fallback用（baseW推定できないページなど）
    scale: { type: Number, default: 1 },
    renderAllPages: { type: Boolean, default: true },
    // A案：OCR baseW に合わせて描画するときの安全上限（重すぎ防止）
    maxRenderScale: { type: Number, default: 3 },
    pageImageSizes: { type: Object, default: () => ({}) },
  },
  emits: ['rendered', 'error'],
  setup(props, { emit }) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;
    const isRendered = ref(false);
    const pane = ref(null);
    const pages = reactive([]); // [{ pageNumber, w, h }]

    // 自動算出したFSH基準サイズ
    const autoBaseSizeMap = reactive({}); // { [pn]: { w, h } }

    const canvasMap = new Map(); // pn -> canvas
    const viewportMap = new Map(); // pn -> viewport（描画に使ったscale付き）
    const canvasCssMap = new Map(); // pn -> { w, h } （overlay変換に使う）
    const pageScaleMap = new Map(); // pn -> renderScale（A案）

    // FSHの仕様: テキストPDFは200dpi
    const FSH_TEXT_DPI = 200;
    const FSH_MAX_PX = 4096;

    let destroyed = false;
    let pdfDoc = null;
    // 日本語(CID-keyed)フォントのPDFを描画するためにCMap/標準フォントを指定する。
    // 未指定だと該当フォントのグリフが描画されず、文字だけが消える。
    const PDF_FONT_OPTIONS = {
      cMapUrl: '/cmaps/',
      cMapPacked: true,
      standardFontDataUrl: '/standard_fonts/',
    };
    const getDocumentSrc = () => {
      if (props.pdfUrl instanceof Uint8Array) {
        return { data: props.pdfUrl, ...PDF_FONT_OPTIONS };
      }
      return { url: props.pdfUrl, ...PDF_FONT_OPTIONS };
    };
    const clear = () => {
      pages.splice(0, pages.length);
      canvasMap.clear();
      viewportMap.clear();
      canvasCssMap.clear();
      pageScaleMap.clear();
    };
    const highlightsByPage = pageNumber =>
      props.highlights.filter(
        h => Number(h.ocrFieldSetPage) === Number(pageNumber),
      );

    const calcRenderScaleForPage = (page, pageNumber) => {
      const vp1 = page.getViewport({ scale: 1 });
      // OCR基準横幅 ÷ PDF実幅
      let scale = props.ocrBaseWidth / vp1.width;
      // 安全上限
      scale = Math.min(scale, props.maxRenderScale);
      if (!isFinite(scale) || scale <= 0) {
        scale = props.scale;
      }
      return scale;
    };

    const calcFshBaseSizeForPage = page => {
      // PDF points (72dpi相当) のページサイズ
      const v1 = page.getViewport({ scale: 1 });
      // 200dpiに変換した場合のピクセル
      const rawW = (v1.width / 72) * FSH_TEXT_DPI;
      const rawH = (v1.height / 72) * FSH_TEXT_DPI;
      // 4096制限（FSHのロジックに合わせる）
      const s = Math.min(1, FSH_MAX_PX / rawW, FSH_MAX_PX / rawH);
      return {
        w: Math.round(rawW * s),
        h: Math.round(rawH * s),
      };
    };

    const render = async () => {
      isRendered.value = false;
      try {
        destroyed = false;
        clear();
        const loadingTask = pdfjsLib.getDocument(getDocumentSrc());
        pdfDoc = await loadingTask.promise;
        if (destroyed) return;
        const pageNumbers = props.renderAllPages
          ? Array.from({ length: pdfDoc.numPages }, (_, i) => i + 1)
          : Array.from(
              new Set(props.highlights.map(h => Number(h.ocrFieldSetPage))),
            ).sort((a, b) => a - b);
        // 先にページ枠（viewportサイズ）を作る
        for (const pn of pageNumbers) {
          const page = await pdfDoc.getPage(pn);

          autoBaseSizeMap[pn] = calcFshBaseSizeForPage(page);

          if (destroyed) return;
          const renderScale = calcRenderScaleForPage(page, pn);
          pageScaleMap.set(pn, renderScale);
          const viewport = page.getViewport({ scale: renderScale });
          viewportMap.set(pn, viewport);
          pages.push({
            pageNumber: pn,
            w: Math.ceil(viewport.width),
            h: Math.ceil(viewport.height),
          });
        }
        await nextTick();
        // canvas参照を確保
        for (const p of pages) {
          const pn = p.pageNumber;
          const canvas = pane.value?.querySelector(
            `canvas[data-page-number="${pn}"]`,
          );
          if (canvas) canvasMap.set(pn, canvas);
        }
        // 実描画
        for (const p of pages) {
          const pn = p.pageNumber;
          const page = await pdfDoc.getPage(pn);
          if (destroyed) return;
          const canvas = canvasMap.get(pn);
          if (!canvas) continue;
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          // ★ page-wrap の実表示幅（親の幅に追従した結果）
          // canvasは width:100% なので、wrap幅を基準にするのが安定
          const wrap = canvas.parentElement; // .page-wrap の想定（構造的に canvas の親）
          const cssW = wrap
            ? wrap.clientWidth
            : canvas.getBoundingClientRect().width;
          // まだレイアウト確定前で 0 のことがあるのでガード
          if (!cssW || cssW <= 0) continue;
          // PDF本来のサイズ（72dpi相当の “ポイント”）
          const vp1 = page.getViewport({ scale: 1 });
          // 表示幅にフィットする scale を作る
          const renderScale = cssW / vp1.width;
          // 倍率調整用（高くなりすぎないように）
          const safeScale = Math.min(renderScale, props.maxRenderScale || 3);
          // 「見た目の幅＝wrap幅」になるviewport
          const viewport = page.getViewport({ scale: safeScale });
          viewportMap.set(pn, viewport);
          // 見た目の高さ（cssH）も viewport から決める
          const cssH = viewport.height;
          // canvas の CSS 表示は 100% 追従にする（←ここが重要）
          canvas.style.width = '100%';
          canvas.style.height = cssH + 'px';
          // DPR（画質改善）
          const dpr = Math.min(window.devicePixelRatio || 1, 2);
          // 内部解像度をDPR分だけ増やす（表示サイズ×DPR）
          canvas.width = Math.floor(cssW * dpr);
          canvas.height = Math.floor(cssH * dpr);
          // DPR座標系に合わせる
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          await page.render({ canvasContext: ctx, viewport }).promise;
        }

        await nextTick();
        updateCanvasCssMap();

        isRendered.value = true;
        emit('rendered', { numPages: pdfDoc.numPages });
      } catch (e) {
        emit('error', e);
      }
    };
    // ハイライト：OCR座標(px, 左上起点) -> canvas CSS(px)
    const highlightStyle = (h, pageNumber) => {
      const pn = Number(pageNumber);
      if (!isRendered.value) return {};
      const css = canvasCssMap.get(pn);
      if (!css) return {};
      // 親から渡された値があればそれを優先。なければ自動算出を使う
      const base =
        (props.pageImageSizes &&
          (props.pageImageSizes[pn] || props.pageImageSizes[String(pn)])) ||
        autoBaseSizeMap[pn];
      if (!base || !base.w || !base.h) return {};
      const sx = css.w / Number(base.w);
      const sy = css.h / Number(base.h);
      const x = Number(h.ocrFieldXCoordinate);
      const y = Number(h.ocrFieldYCoordinate);
      const w = Number(h.ocrFieldWidth);
      const hh = Number(h.ocrFieldHeight);
      if (![x, y, w, hh].every(v => isFinite(v))) return {};
      return {
        left: x * sx + 'px',
        top: y * sy + 'px',
        width: w * sx + 'px',
        height: hh * sy + 'px',
      };
    };

    const getScrollParent = el => {
      let cur = el;
      while (cur && cur !== document.body) {
        const style = window.getComputedStyle(cur);
        const overflowY = style.overflowY;
        if (
          /(auto|scroll)/.test(overflowY) &&
          cur.scrollHeight > cur.clientHeight
        ) {
          return cur;
        }
        cur = cur.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    };

    const scrollToActive = async h => {
      if (!h) return;
      await nextTick();
      const pn = Number(h.ocrFieldSetPage);
      if (!pn) return;
      const canvas = canvasMap.get(pn);
      if (!canvas) return;
      const pageWrap = canvas.closest ? canvas.closest('.page-wrap') : null;
      if (!pageWrap) return;
      // スクロールしてる要素を特定
      const scroller = getScrollParent(pane.value || pageWrap);
      // console.log('[scroller]', {
      //   tag: scroller.tagName,
      //   class: scroller.className,
      //   clientHeight: scroller.clientHeight,
      //   scrollHeight: scroller.scrollHeight,
      //   overflowY: window.getComputedStyle(scroller).overflowY,
      // });
      // base（基準画像サイズ）
      const base =
        (props.pageImageSizes &&
          (props.pageImageSizes[pn] || props.pageImageSizes[String(pn)])) ||
        autoBaseSizeMap[pn];
      if (!base || !base.w || !base.h) return;
      // canvas の表示サイズ（CSS px）
      const canvasRect = canvas.getBoundingClientRect();
      if (!canvasRect.width || !canvasRect.height) return;
      const sx = canvasRect.width / Number(base.w);
      const sy = canvasRect.height / Number(base.h);
      const x = Number(h.ocrFieldXCoordinate);
      const y = Number(h.ocrFieldYCoordinate);
      if (![x, y].every(v => isFinite(v))) return;
      const targetTopInCanvas = y * sy;
      // scroller基準で計算
      const scrollerRect = scroller.getBoundingClientRect();
      const wrapRect = pageWrap.getBoundingClientRect();
      const wrapTopInScrollerScroll =
        wrapRect.top - scrollerRect.top + scroller.scrollTop;
      const marginTop = 24;
      const targetScrollTop =
        wrapTopInScrollerScroll + targetTopInCanvas - marginTop;

      scroller.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth',
      });
    };
    watch(() => props.pdfUrl, () => render());
    watch(() => props.scale, () => render());
    watch(() => props.maxRenderScale, () => render());
    watch(
      () => props.highlights,
      async () => {
        // highlightsが後から来るケース：baseW推定が変わるので描画し直す
        await nextTick();
        render();
      },
      { deep: true },
    );
    watch(
      () => {
        const a = props.activeId;
        if (!a) return '';
        return [
          a.twFieldPath,
          a.ocrFieldSetPage,
          a.ocrFieldXCoordinate,
          a.ocrFieldYCoordinate,
        ].join('|');
      },
      key => {
        if (!key) return;
        if (!isRendered.value) return;
        scrollToActive(props.activeId);
      },
    );

    let ro = null;

    const updateCanvasCssMap = () => {
      pages.forEach(p => {
        const pn = p.pageNumber;
        const canvas = canvasMap.get(pn);
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        canvasCssMap.set(pn, { w: rect.width, h: rect.height });
      });
    };
    onMounted(() => {
      render();
      if (window.ResizeObserver) {
        ro = new ResizeObserver(() => {
          nextTick(() => updateCanvasCssMap());
        });
        if (pane.value) ro.observe(pane.value);
      } else {
        window.addEventListener('resize', updateCanvasCssMap);
      }
    });
    onBeforeUnmount(() => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', updateCanvasCssMap);
      destroyed = true;
      try {
        pdfDoc?.destroy?.();
      } catch (e) {}
    });
    return {
      isRendered,
      pane,
      pages,
      highlightsByPage,
      highlightStyle,
    };
  },
});
</script>

<style scoped>
.pdf-pane {
  overflow: auto;
  border: 1px solid #ddd;
  padding: 8px;
  width: 100%;
  min-width: 320px; /* ★ 最低幅 */
}
.page-wrap {
  position: relative;
  margin-bottom: 12px;
  width: 100%; /* ★ 親幅に合わせる */
  min-width: 320px; /* ★ 最低幅 */
}
.pdf-canvas {
  display: block;
  width: 100%; /* ★ 親幅に合わせる */
  height: auto; /* ★ 縦横比維持 */
}
.overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
/* ハイライト矩形 */
.hl {
  position: absolute;
  border: 2px solid rgba(0, 150, 255, 0.9);
  background: rgba(0, 150, 255, 0.25);
  border-radius: 4px;
  box-sizing: border-box;
}
</style>
